import React, { FC, MouseEvent, useCallback, useContext, useState } from 'react';
import { Icon, IconEnum } from '@investoro/core';
import clsx from 'clsx';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import ChatbotContext from 'utils/context/ChatbotContext';
import { formatDate } from 'utils/formatters/formatDate';
import { AllModuleTypes } from 'utils/types/AllModules';
import { useWindowWidth } from 'utils/useWindowWidth';

import AiOpinionBox from 'components/common/AiOpinionBox';
import NoteBox, { Note } from 'components/common/NoteBox';
import Slider, { SliderImages } from 'components/common/Slider/Slider';
import { TabType } from 'components/common/Tabs/V2Tabs';

import InfoBadge from './common/InfoBadge';
import V2CardBodyDefault, { V2CardBodyProperties } from './common/V2CardBodyDefault/V2CardBodyDefault';

const Container = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
    border-radius: var(--box-border-radius);
    background-color: var(--color-white);
    padding: 15px;
    gap: 22px;
    cursor: pointer;
    transition: box-shadow 0.3s ease-in-out;
    -webkit-tap-highlight-color: transparent;


    & > div:nth-child(1) {
        width: 100%;
    }

    & > div:nth-child(2) {
        width: 100%;
        height: auto;
    }

    &.grey {
        opacity: 0.5;
    }
    
    @media (min-width: 991px) {
        grid-template-columns: 240px 1fr;

        &.bigImage {
            grid-template-columns: 300px 1fr;
        }

        & > div:nth-child(1) {
            width: 240px;
        }

        & > div:nth-child(2) {
            width: 100%;
            height: auto;
        }
    }
`;

export const InfoBoxContainer = styled.div`
    position: absolute;
    top: 0;
    width: fit-content;
    display: flex;
    flex-direction: column;
    padding: 10px;
    gap: 10px;
    z-index: 99;
    pointer-events: none;

    > * {
        pointer-events: auto;
    }
`;

const ImageColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ChatButton = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--label-color-active);
    color: var(--color-primary);
    border: none;
    border-radius: var(--box-border-radius);
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    width: 100%;
    transition: opacity 0.2s ease-in-out;

    &:hover {
        color: var(--color-white);
        background: var(--color-alt);

        img {
            filter: invert(100%) sepia(100%) grayscale(100%) brightness(200%);
        }
    }
`;

const OldestDuplicateBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 14px;
    color: var(--color-text-secondary);
    font-size: 12px;
    white-space: nowrap;

    img {
        width: 16px;
        height: 16px;
    }

    b {
        color: var(--color-primary);
        font-weight: 600;
    }
`;

const CardImageContainer = styled.div`
    background: var(--color-alt-second-10);
    border-radius: var(--box-border-radius);
    overflow: hidden;
    position: relative;
    height: 240px;

    &.big {
        height: 250px;
        min-width: 300px;

        @media (max-width: 991px) {
            height: 240px;
            min-width: 240px;
        }
    }
`;

const ImgStyled = styled.img`
    object-fit: cover;
    width: 100%;
    height: 100%;
    transition: transform 0.3s ease-in-out;

`;

interface AiOpinionType {
    content: string;
    contentWeb: string;
    contentMobile: string;
    dateAdded: string;
    lengthMoreThanMobile: boolean;
    lengthMoreThanWeb: boolean;
}

export type CardData = {
    date?: string;
    dateIcon?: IconEnum;
    oldestDuplicateDateAdded?: string;
    modificationDate?: string;
    additionalInfoBadge?: string;
    predictedAdditionalInfoBadge?: string;
    secondAdditionalInfoBadge?: string;
    images?: SliderImages[];
    thumbnailPath?: string;
    note?: Note;
    notes?: Note[];
    numberOfNotes?: number;
    getAllNotes?: (id: string, module: AllModuleTypes, getNotes?: boolean) => Promise<Note[] | void>;
    offerId?: string | number;
    offerModule?: AllModuleTypes;
    aiOpinion?: AiOpinionType;
    adContext?: { adId: number | string; adModule: string };
} & V2CardBodyProperties;

export interface V2BaseCardProperties {
    onClick?: (event: React.MouseEvent) => void;
    openOfferTab: (tab: TabType) => void;
    cardData: CardData;
    bigImage?: boolean;
    isHalfTransparent?: boolean;
}

const V2BaseCard: FC<V2BaseCardProperties> = ({
    onClick,
    openOfferTab,
    cardData,
    bigImage,
    isHalfTransparent
}) => {
    const { date, modificationDate, additionalInfoBadge, predictedAdditionalInfoBadge, images, dateIcon, oldestDuplicateDateAdded } = cardData;
    const [offerDate, setOfferDate] = useState(modificationDate || date);
    const [isSuccess, setIsSuccess] = useState(!modificationDate);
    const width = useWindowWidth();
    const { user } = useUser();
    const { openWithAdContext } = useContext(ChatbotContext);

    const handleOpenOfferTab = (tab: TabType) => {
        openOfferTab(tab);
    };

    const handleBadgeChange = (e: MouseEvent) => {
        if (modificationDate && modificationDate !== date && width <= 1200) {
            e.stopPropagation();
            setOfferDate((prevDate) => prevDate === modificationDate ? date : modificationDate);
            setIsSuccess((prev) => !prev);
        }
    };

    const onMouseLeave = useCallback(() => {
        if (modificationDate && width > 1100) {
            setTimeout(() => {
                setOfferDate(modificationDate);
                setIsSuccess(false);
            }, 150);
        }
    }, [width, modificationDate]);

    const onMouseEnter = useCallback(() => {
        if (modificationDate && modificationDate !== date && width > 1100) {
            setTimeout(() => {
                setOfferDate(date);
                setIsSuccess(true);
            }, 150);
        }
    }, [
        modificationDate,
        width,
        date
    ]);

    return (
        <Container onClick={onClick} className={clsx({ bigImage: bigImage, grey: isHalfTransparent })}>
            <ImageColumn>
                <CardImageContainer className={clsx({ big: bigImage })}>
                    {images && images.length > 0
                        ? <Slider images={images} title={cardData.title} isHovered/>
                        : <ImgStyled
                            src={cardData.thumbnailPath && !cardData.thumbnailPath.includes('fileName=null') ? cardData.thumbnailPath : '/defaultImg.png'}
                            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                e.currentTarget.src = '/defaultImg.png';
                            }} />}
                    <InfoBoxContainer>
                        {offerDate
                            ? <InfoBadge onClick={width <= 1200 ? handleBadgeChange : undefined}
                                isDarkMode={user.isDarkMode}
                                onMouseLeave={onMouseLeave}
                                onMouseEnter={onMouseEnter}
                                fixedWidth
                                icon={dateIcon}
                                isSuccess={isSuccess}
                                isWarning={!isSuccess}
                            >
                                {formatDate(offerDate)}
                            </InfoBadge>
                            : null}
                        {(additionalInfoBadge || predictedAdditionalInfoBadge) && <InfoBadge isWhite predictedValue={predictedAdditionalInfoBadge}>
                            {additionalInfoBadge}
                        </InfoBadge>}
                    </InfoBoxContainer>
                </CardImageContainer>
                {cardData.adContext &&
                    <ChatButton
                        onClick={(e: MouseEvent) => {
                            e.stopPropagation();
                            openWithAdContext(cardData.adContext!.adId, cardData.adContext!.adModule);
                        }}
                    >
                        <Icon icon={IconEnum.MESSAGE} />
                        Porozmawiaj o ogłoszeniu
                    </ChatButton>
                }
                {oldestDuplicateDateAdded &&
                    <OldestDuplicateBadge>
                        <Icon icon={IconEnum.CALENDAR} />
                        Pierwsza publikacja: <b>{formatDate(oldestDuplicateDateAdded)}</b>
                    </OldestDuplicateBadge>
                }
            </ImageColumn>
            <V2CardBodyDefault {...{ ...cardData, openOfferTab }} />
            {cardData.note &&
                <NoteBox
                    newestNote={cardData.note}
                    numberOfNotes={cardData.numberOfNotes}
                    getAllNotes={cardData.getAllNotes}
                    openOfferTab={() => handleOpenOfferTab(TabType.NOTES)}
                    offerId={cardData.offerId}
                    offerModule={cardData.offerModule}
                />
            }
            {cardData.aiOpinion &&
                <AiOpinionBox
                    content={width <= 768 ? cardData.aiOpinion.contentMobile : cardData.aiOpinion.contentWeb}
                    onClick={() => handleOpenOfferTab(TabType.OPINIONS)}
                />
            }
        </Container>
    );
};

export default V2BaseCard;
